import React, { useState } from "react";
import { money } from "@/lib/format";
import { ChevronDown, ChevronRight, MousePointerClick } from "lucide-react";

export interface PlatformMonthRow {
  month: string;
  provider: string;
  channel: string;
  ventas: number;
  comisiones: number;
  pasarela: number;
  retenciones: number;
  servicios: number;
  reembolsos: number;
  ajustes: number;
  neto: number;
  depositado: number;
  pendiente: number;
  cobrado: number;
}

const COLS: [keyof PlatformMonthRow, string][] = [
  ["ventas", "Ventas"],
  ["comisiones", "Comisiones"],
  ["pasarela", "Pasarela"],
  ["retenciones", "Retenciones"],
  ["servicios", "Cargos servicio"],
  ["reembolsos", "Reembolsos"],
  ["ajustes", "Ajustes"],
  ["neto", "Neto a recibir"],
  ["cobrado", "Ya depositado"],
  ["pendiente", "Pendiente"],
];

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const monthLabel = (m: string) => `${MONTHS[parseInt(m.slice(5, 7), 10) - 1]} ${m.slice(0, 4)}`;

const Cell: React.FC<{ k: keyof PlatformMonthRow; v: number; bold?: boolean }> = ({ k, v, bold }) => (
  <td className={`px-3 py-2.5 text-right tabular ${bold || k === "neto" || k === "cobrado" ? "font-medium" : ""} ${
    k === "pendiente" ? (v > 0 ? "font-medium text-amber-600" : "text-slate-400") : k === "ventas" || k === "cobrado" ? "text-slate-900" : v < 0 ? "text-red-600" : v > 0 && k !== "neto" ? "text-emerald-600" : "text-slate-700"
  }`}>
    {v === 0 ? "—" : money(v)}
  </td>
);

export const PlatformMonthlyTable: React.FC<{ rows: PlatformMonthRow[]; selected?: string; onSelect: (month: string, channel: string) => void }> = ({ rows, selected, onSelect }) => {
  const months = Array.from(new Set(rows.map((r) => r.month)));
  const [open, setOpen] = useState<Set<string>>(new Set(months.slice(0, 1)));
  const toggle = (m: string) => setOpen((p) => { const n = new Set(p); if (n.has(m)) n.delete(m); else n.add(m); return n; });
  return (
    <div className="mb-6 overflow-x-auto border border-slate-200 bg-white" data-testid="platform-monthly-table">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-red-50/40 px-4 py-2 text-xs text-slate-600" data-testid="platform-table-hint">
        <MousePointerClick className="h-3.5 w-3.5 text-[#DD0000]" />
        Da clic en el mes para desplegarlo y en una plataforma para ver abajo el desglose de sus movimientos.
      </div>
      <table className="w-full min-w-[1000px] text-sm">
        <thead>
          <tr className="bg-slate-50/80 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3">Mes</th>
            <th className="px-3 py-3">Plataforma</th>
            {COLS.map(([, l]) => (
              <th key={l} className="px-3 py-3 text-right">{l}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {months.map((m) => {
            const mrows = rows.filter((r) => r.month === m);
            const tot = Object.fromEntries(COLS.map(([k]) => [k, mrows.reduce((s, r) => s + (r[k] as number), 0)]));
            return (
              <React.Fragment key={m}>
                <tr
                  onClick={() => toggle(m)}
                  className="cursor-pointer border-b border-slate-200 bg-slate-50/60 transition-colors hover:bg-slate-100"
                  data-testid={`platform-total-${m}`}
                >
                  <td className="px-4 py-2.5 font-medium capitalize text-slate-800">
                    <span className="inline-flex items-center gap-1.5">
                      {open.has(m) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {monthLabel(m)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500">Total</td>
                  {COLS.map(([k]) => <Cell key={k} k={k} v={Math.round(tot[k] * 100) / 100} bold />)}
                </tr>
                {open.has(m) && mrows.map((r) => (
                  <tr
                    key={r.provider}
                    onClick={() => onSelect(m, r.channel)}
                    className={`cursor-pointer border-b border-slate-50 transition-colors hover:bg-red-50/40 ${selected === `${m}|${r.channel}` ? "bg-red-50/60" : ""}`}
                    data-testid={`platform-row-${m}-${r.provider}`}
                  >
                    <td className="px-4 py-2.5" />
                    <td className="px-3 py-2.5 text-slate-600">{r.channel}</td>
                    {COLS.map(([k]) => <Cell key={k} k={k} v={r[k] as number} />)}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
        Por mes de venta: Neto a recibir = ventas − comisiones − retenciones − cargos ± ajustes de ese mes = Ya depositado + Pendiente.
      </p>
    </div>
  );
};
